import { useState } from "react";
import { isSortableOperation } from "@dnd-kit/react/sortable";
import {
  getAddressKey,
  getAddressLabel,
  moveItem,
  normalizeAddress,
} from "../helpers/addressRouteHelpers";

export const useSubRoutes = ({ bookingData, onError, messages }) => {
  const [addressDestination, setAddressDestination] = useState(
    bookingData?.destination ? normalizeAddress(bookingData.destination) : null,
  );
  const [serviceList, setServiceList] = useState(
    bookingData ? bookingData.serviceList : [],
  );
  const [subRoutesList, setSubRoutesList] = useState(() => {
    if (!bookingData?.destination) return [];
    return [
      ...(bookingData?.serviceList ?? []),
      normalizeAddress(bookingData.destination),
    ].map(normalizeAddress);
  });
  const [openDestinationSelect, setOpenDestinationSelect] = useState(false);
  const [openSubRoutesModal, setOpenSubRoutesModal] = useState(false);
  const [subRouteSelected, setSubRouteSelected] = useState(null);
  const [destinationInputValue, setDestinationInputValue] = useState("");

  const addressDestinationText =
    subRoutesList.length > 1
      ? `${subRoutesList.length} PARADAS...`
      : getAddressLabel(addressDestination);

  const syncSubRoutes = (nextRoutes) => {
    if (nextRoutes.length === 0) {
      setSubRoutesList([]);
      setServiceList([]);
      setAddressDestination(null);
      return;
    }

    const normalizedRoutes = nextRoutes.map(normalizeAddress);
    const nextDestination = normalizedRoutes[normalizedRoutes.length - 1];
    setSubRoutesList(normalizedRoutes);
    setServiceList(normalizedRoutes.slice(0, -1));
    setAddressDestination(nextDestination);
  };

  const handleDestinationChange = (newValue) => {
    if (!newValue) {
      syncSubRoutes([]);
      setDestinationInputValue("");
      return;
    }

    syncSubRoutes([newValue]);
    setDestinationInputValue("");
    setOpenDestinationSelect(false);
  };

  const handleOpenSubRoutesModal = () => {
    if (addressDestination) setOpenSubRoutesModal(true);
  };

  const handleCloseSubRoutesModal = () => {
    setOpenSubRoutesModal(false);
    setSubRouteSelected(null);
  };

  const handleAddSubRoute = (selectedRoute = subRouteSelected) => {
    if (!selectedRoute) {
      onError(messages.selectSubRoute);
      return;
    }

    const selectedKey = getAddressKey(selectedRoute);
    const lastRoute = subRoutesList[subRoutesList.length - 1];
    const isConsecutiveDuplicate =
      lastRoute && getAddressKey(lastRoute) === selectedKey;

    if (isConsecutiveDuplicate) {
      onError(messages.duplicateSubRoute);
      return;
    }

    syncSubRoutes([...subRoutesList, selectedRoute]);
    setSubRouteSelected(null);
  };

  const handleRemoveSubRoute = (routeIndex) => {
    if (subRoutesList.length <= 1) return;
    syncSubRoutes(subRoutesList.filter((_, index) => index !== routeIndex));
  };

  const handleSubRoutesDragEnd = (event) => {
    if (event.canceled || !isSortableOperation(event.operation)) return;

    const fromIndex = event.operation.source.initialIndex;
    const toIndex = event.operation.source.index;

    if (
      !Number.isInteger(fromIndex) ||
      !Number.isInteger(toIndex) ||
      fromIndex < 0 ||
      toIndex < 0 ||
      fromIndex >= subRoutesList.length ||
      toIndex >= subRoutesList.length
    ) return;
    if (fromIndex === toIndex) return;

    const nextRoutes = moveItem(subRoutesList, fromIndex, toIndex);
    const hasConsecutiveDuplicates = nextRoutes.some(
      (route, index) =>
        index > 0 &&
        getAddressKey(route) === getAddressKey(nextRoutes[index - 1]),
    );

    if (hasConsecutiveDuplicates) {
      onError(messages.duplicateSubRoute);
      return;
    }

    syncSubRoutes(nextRoutes);
  };

  const resetSubRoutes = () => {
    syncSubRoutes([]);
    setSubRouteSelected(null);
    setDestinationInputValue("");
  };

  return {
    addressDestination,
    serviceList,
    subRoutesList,
    openDestinationSelect,
    setOpenDestinationSelect,
    openSubRoutesModal,
    subRouteSelected,
    setSubRouteSelected,
    destinationInputValue,
    setDestinationInputValue,
    addressDestinationText,
    handleDestinationChange,
    handleOpenSubRoutesModal,
    handleCloseSubRoutesModal,
    handleAddSubRoute,
    handleRemoveSubRoute,
    handleSubRoutesDragEnd,
    resetSubRoutes,
  };
};
